import { get } from "@/lib/http";
import { PlanActionsEnum } from "@/types/plan.type";
import { createZitadelAuth, ZitadelConfig } from "@zitadel/react";
import { User } from "oidc-client-ts";
import { toast } from "sonner";
import { proxy, snapshot, useSnapshot } from "valtio";
import { getStripeProxy } from "./stripe.store";

const zitadelConfig: ZitadelConfig = {
  authority: window.VITE_ZITADEL_AUTHORITY,
  redirect_uri: window.VITE_ZITADEL_REDIRECT_URL,
  client_id: window.VITE_ZITADEL_CLIENT_ID,
  project_resource_id: window.VITE_ZITADEL_RESOURCE_ID,
  scope: window.VITE_ZITADEL_SCOPE,
  metadataUrl: window.VITE_ZITADEL_METADATA_URL
};

const zitadelManager = createZitadelAuth(zitadelConfig);
const userManager = zitadelManager.userManager;

userManager.startSilentRenew();

userManager.events.addAccessTokenExpired(async () => {
  console.warn("User token is expired");
  localStorage.removeItem("token");
  accountProxy.userInfo = undefined;
});

userManager.events.addAccessTokenExpiring(async () => {
  console.warn("User token is expiring soon");
  try {
    const user = await userManager.getUser();
    if (user && !user.expired) {
      accountProxy.userInfo = user;
      localStorage.setItem("token", `Bearer ${user.access_token}`);
    } else {
      accountProxy.userInfo = undefined;
    }
  } catch (err: unknown) {
    console.error("Error happened when trying to refresh the token");
  }
});

// ---------------
// External Stores
// ---------------
const stripeProxy = getStripeProxy();

const accountProxy = proxy({
  isSigning: false,
  isLoading: false,
  userInfo: undefined as User | undefined,
  organizationLogoUrl: "",

  get isLogged(): boolean {
    return (
      !!accountProxy.userInfo &&
      !accountProxy.isLoading &&
      !accountProxy.isSigning
    );
  },

  login: () => {
    return zitadelManager.authorize();
  },

  get accessToken(): string {
    return localStorage.getItem("token") ?? "";
  },

  completeLogin: async () => {
    accountProxy.isSigning = true;
    try {
      const user = await userManager.signinRedirectCallback();

      accountProxy.userInfo = user;
      localStorage.setItem("token", `Bearer ${user.access_token}`);
      if (user.refresh_token) {
        localStorage.setItem("refresh_token", `${user.refresh_token}`);
      }
    } catch (err: unknown) {
      //TODO: Do something with errors
      console.log(err);
      toast.error("Error happened during authentication");
    } finally {
      accountProxy.isSigning = false;
      accountProxy.isLoading = false;
    }
  },

  loadUser: async () => {
    accountProxy.isLoading = true;
    try {
      const user = await userManager.getUser();
      if (user && !user.expired) {
        accountProxy.userInfo = user;
        localStorage.setItem("token", `Bearer ${user.access_token}`);
      } else {
        accountProxy.userInfo = undefined;
      }
    } catch (err: unknown) {
      //TODO: Do something with errors
      console.log(err);
      toast.error("Error happened during authentication");
    } finally {
      accountProxy.isLoading = false;
      accountProxy.isSigning = false;
    }
  },

  getPaymentMethods: async () => {
    if (!accountProxy.userInfo) {
      console.error("Could not retrieve current user info");
      toast.error("Could not retrieve current user info");
      throw new Error("Could not retrieve current user info");
    }

    const externalId = accountProxy.userInfo.profile[
      "urn:zitadel:iam:user:resourceowner:id"
    ] as string;

    return stripeProxy.getAccountPaymentMethods(externalId);
  },

  regenerateCheckoutUrl: async () => {
    if (!accountProxy.userInfo) {
      console.error("Could not retrieve current user info");
      toast.error("Could not retrieve current user info");
      throw new Error("Could not retrieve current user info");
    }
    const externalId = accountProxy.userInfo.profile[
      "urn:zitadel:iam:user:resourceowner:id"
    ] as string;

    return stripeProxy.generateAccountCheckoutUrl(externalId);
  },

  setupPaymentMethod: async (
    previousPage?: string,
    action?: PlanActionsEnum
  ) => {
    if (!accountProxy.userInfo) {
      console.error("Could not retrieve current user info");
      toast.error("Could not retrieve current user info");
      throw new Error("Could not retrieve current user info");
    }
    const externalId = accountProxy.userInfo.profile[
      "urn:zitadel:iam:user:resourceowner:id"
    ] as string;

    return stripeProxy.setupAccountPaymentMethods(
      externalId,
      previousPage,
      action
    );
  },

  logout: () => {
    accountProxy.userInfo = undefined;
    localStorage.clear();
    toast.info("You have been logged out");
  },

  getOrganizationLogoUrl: async () => {
    const response = await get<string>(
      `/api/account/${accountProxy.userInfo?.profile["urn:zitadel:iam:user:resourceowner:id"]}/organization-logo/`
    );
    accountProxy.organizationLogoUrl = response;

    // Setting up favicon
    const link: any = document.querySelector("link[rel~='icon']");
    link.href = accountProxy.organizationLogoUrl;
  },

  async verifyEmail(userID: string, code: string) {
    try {
      const response = await get<boolean>(
        `/api/account/${userID}/verify?code=${code}`
      );
      if (!response) {
        throw new Error("Failed to verify user");
      } else {
        toast.success("User successfully validated");
      }
      return response;
    } catch (error) {
      toast.error("Failed to verify User");
      throw new Error("Failed to verify user");
    }
  }
});

export const getAccountSnapshot = () => snapshot(accountProxy);
export const getAccountProxy = () => accountProxy;

export const useAccountSnapshot = (
  options?: Parameters<typeof useSnapshot>[1]
) => useSnapshot(accountProxy, options);

export const useAccountProxy = () => accountProxy;

export const useAccountStore = (
  options?: Parameters<typeof useSnapshot>[1]
) => {
  return {
    accountSnapshot: useSnapshot(accountProxy, options),
    accountProxy: accountProxy
  };
};
